import { FontIcon, Spinner, SpinnerSize, Text, TooltipHost, TooltipOverflowMode } from "@fluentui/react";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { DragAndDropHelper } from "../../../../../../../utilities/DragAndDropHelper";
import { LanguageContext } from "../../../../../../../utilities/LocalizationModule";
import { dropZoneStyleSets } from "../../../../../config/controlStyles";
import { ITeamChannelDto, ITeamChannelLocationDto } from "../../../../../interfaces/services/ITeamChannelDto";
import { addUploadedFiles } from "../../../../../redux/modules/file";
import { smallPadLeft } from "../../../../../styles/iconStyles";
import { mobileListItemStyle } from "../../../../../styles/mobileStyles";
import { buildWorkingDirectoryFromTree, findWorkingDirectories, treeViewItemStyle } from "../../../../helpers/treeHelperGeneral";
import { RootState } from "../../../redux/storeMain";
import { logger } from "../../../../../../../utilities/logger";
import { ChannelPrivateIcon } from "../../SvgIcons/ChannelPrivateIcon";
import { ChannelShareIcon } from "../../SvgIcons/ChannelShareIcon";
import { ArrowClockwise20Regular } from "../../SvgIcons/ArrowClockwise20Regular";
import { Channel20Regular } from "../../SvgIcons/Channel20Regular";
import { MoreVertical20Regular } from "../../SvgIcons/MoreVertical20Regular";
import { Save20Regular } from "../../SvgIcons/Save20Regular";

interface ITeamChannelItemProps {
    item?: ITeamChannelDto;
    treeItem?: ITeamChannelLocationDto;
    isMobile: boolean;
    isValidDropZone: boolean;
    hasFileSelected: boolean;
    allowScroll?: boolean;
    getTreeLocations?: () => any[];
    onChannelClicked: (event: React.SyntheticEvent, item: ITeamChannelDto, isSaveRequest?: boolean, e?: any) => Promise<void>;
    onTreeItemClick?: (event: React.SyntheticEvent, treeItem: any) => Promise<void>;
    openFolderOptions?: (teamChannel: ITeamChannelDto) => Promise<void>;
    openFolderOptionsTree?: (e: any) => void;
    onTreeExpand?: (isRefresh: boolean) => void;
    inLocationsComponent?: boolean;
    inTeamsComponent?: boolean;
    inSharepointComponent?: boolean;
}

export const TeamChannelItem = ({ item, treeItem, isMobile, isValidDropZone, hasFileSelected, allowScroll, getTreeLocations, onChannelClicked, onTreeItemClick, openFolderOptions, openFolderOptionsTree, onTreeExpand, inLocationsComponent, inTeamsComponent, inSharepointComponent }: ITeamChannelItemProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const dispatch = useDispatch();

    const user = useSelector((state: RootState) => state.user);

    const [isDraggedOver, setIsDraggedOver] = React.useState(false);
    const dragCounter = React.useRef<number>(0);

    const dragEnter = React.useCallback(async (event: React.DragEvent<HTMLDivElement>) => {
        if (DragAndDropHelper.validateDroppedFiles(event) || DragAndDropHelper.validateDroppedGridItems(event, false, false, undefined, treeItem)) {
            event.preventDefault();
            event.stopPropagation();
            dragCounter.current = dragCounter.current + 1;
            setIsDraggedOver(true);
        }
    }, [treeItem]);

    const dragOver = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
    }, []);

    const dragLeave = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.stopPropagation();
        if (isDraggedOver) {
            dragCounter.current = dragCounter.current - 1;
            if (!dragCounter.current || event.target === event.currentTarget) {
                setIsDraggedOver(false);
            }
        }
    }, [isDraggedOver]);

    const handleDrop = React.useCallback(async (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        dragCounter.current = 0;
        setIsDraggedOver(false);

        try {
            if (DragAndDropHelper.validateDroppedFiles(event)) {
                const uploadedFiles = await DragAndDropHelper.handleDroppedFiles(event)
                dispatch(addUploadedFiles(uploadedFiles));
                onChannelClicked(event, item ? item : treeItem, true);
            }
            else if (DragAndDropHelper.validateDroppedGridItems(event, false, false, undefined, treeItem)) {
                const newWorkingDirectories = findWorkingDirectories(getTreeLocations(), treeItem);
                const { directories } = await buildWorkingDirectoryFromTree(newWorkingDirectories, user);
                const currDirectory = directories[directories.length - 1];

                const siteUrl = currDirectory.webUrl

                const origin = new URL(currDirectory.webUrl).origin;
                const homeItemServerRelPath = `${currDirectory.webUrl.replace(origin, "")}/${currDirectory.directory}`;

                DragAndDropHelper.destinationDirectory = currDirectory;
                DragAndDropHelper.handleGridMove(dispatch, siteUrl, directories, false, homeItemServerRelPath);
            }
        } catch (error) {
            logger(error, "TeamChannelItem.tsx (handleDrop)");
        }
    }, [item, treeItem, onChannelClicked]);

    const className = React.useMemo((): string => {
        let className: string = "list-item";
        if (treeItem) {
            className += ` ${"tree-item"}`;
            if (treeItem.isSelected) {
                className += ` ${"tree-item-selected"}`;
            }
        }

        if (isDraggedOver) {
            className += ` dropzone ${dropZoneStyleSets.dropzoneActive}`;
        }
        else if (isValidDropZone) {
            className += ` dropzone`;
        }
        return className;
    }, [isValidDropZone, treeItem, isDraggedOver]);

    const folderStyles: React.CSSProperties = isMobile ? mobileListItemStyle : {};

    const showChevron = React.useMemo((): boolean => {
        return treeItem && (((treeItem.canExpand && !treeItem.isLoading) || (treeItem.isLoading && treeItem.foldersNextHref)) &&
            !(treeItem.loaded && treeItem.folders?.length === 0));
    }, [treeItem]);

    React.useEffect(() => {
        let debounceTimer;
        if (treeItem && isValidDropZone && isDraggedOver && showChevron && onTreeExpand && !treeItem.expanded && !treeItem.isLoading) {
            debounceTimer = setTimeout(() => {
                onTreeExpand(false);
            }, 1500);
        }

        return () => {
            clearTimeout(debounceTimer);
        };
    }, [treeItem, isValidDropZone, isDraggedOver, showChevron, onTreeExpand]);

    return (
        <div className={className} style={folderStyles} onDragEnter={isValidDropZone ? dragEnter : undefined} onDragOver={isValidDropZone ? dragOver : undefined} onDragLeave={isValidDropZone ? dragLeave : undefined} onDrop={isValidDropZone ? handleDrop : undefined}>
            <div
                className={!allowScroll ? "list-item-link" : "list-item-link-scrollable"}
                onClick={(e) => {
                    e.stopPropagation();
                    if (onTreeItemClick) {
                        onTreeItemClick(e, treeItem);
                    }
                    else if (item) {
                        onChannelClicked(e, item);
                    }
                }}
            >
                {treeItem &&
                    <div style={treeViewItemStyle(treeItem)}
                        onClick={(e) => {
                            if (showChevron) {
                                e.stopPropagation();
                                onTreeExpand(false);
                            }
                        }}>
                        {treeItem.isLoading && !treeItem.foldersNextHref && <Spinner size={SpinnerSize.xSmall} />}
                        {showChevron && <FontIcon iconName={treeItem.expanded ? "ChevronDown" : "ChevronRight"} />}
                    </div>
                }
                <div className="list-item-icon">
                    <Channel20Regular />
                </div>
                <Text className="list-item-name">
                    <TooltipHost content={item ? item.displayName : treeItem?.displayName} overflowMode={TooltipOverflowMode.Parent}>
                        {item ? item.displayName : treeItem?.displayName}
                    </TooltipHost>
                </Text>
                {(item ? item.isPrivate : treeItem?.isPrivate) && <ChannelPrivateIcon style={smallPadLeft} />}
                {(item ? item.isShared : treeItem?.isShared) && <ChannelShareIcon style={smallPadLeft} />}
            </div>
            {treeItem && !treeItem.isLoading && treeItem.loaded && <TooltipHost content={languageStrings.Refresh}>
                <div className={isMobile ? "list-item-save-icon-mobile" : "list-item-save-icon"}>
                    {/* <FontIcon
                        iconName="Refresh"
                        className="refresh-icon"
                        onClick={(e) => {
                            e.stopPropagation();
                            onTreeExpand(true);
                        }}
                    /> */}
                    <ArrowClockwise20Regular
                        onClick={(e) => {
                            e.stopPropagation();
                            onTreeExpand(true);
                        }}
                    />
                </div>
            </TooltipHost>}
            {hasFileSelected && isValidDropZone && !(treeItem && treeItem?.isLoadingChannelInfo) && (
                <TooltipHost content={`${languageStrings.SaveTo} ${item ? item.displayName : treeItem?.displayName}`}>
                    <div className={isMobile ? "list-item-save-icon-mobile" : "list-item-save-icon"}>
                        {/* <FontIcon
                            iconName="Save"
                            onClick={(e) => {
                                e.stopPropagation();
                                onChannelClicked(e, item ? item : treeItem, true);
                            }}
                        /> */}
                        <Save20Regular
                            onClick={(e: any) => {
                                e.stopPropagation();
                                onChannelClicked(e, item ? item : treeItem, true);
                            }}
                        />
                    </div>
                </TooltipHost>
            )}
            <TooltipHost content={languageStrings.MoreActions}>
                {(!treeItem || !treeItem?.isLoadingChannelInfo) && <div className={isMobile ? "list-item-save-icon-mobile" : "list-item-save-icon"}>
                    {/* <FontIcon
                        iconName="More"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (item) {
                                openFolderOptions(item);
                            } else {
                                openFolderOptionsTree(e);
                            }
                        }}
                    /> */}
                    <MoreVertical20Regular
                        onClick={(e) => {
                            e.stopPropagation();
                            if (item) {
                                openFolderOptions(item);
                            } else {
                                openFolderOptionsTree(e);
                            }
                        }}
                    />
                </div>}
                {treeItem && treeItem?.isLoadingChannelInfo && <Spinner size={SpinnerSize.xSmall} styles={{ root: { marginLeft: 8 } }} />}
            </TooltipHost>
        </div>
    )
};